import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Star, Info, Shield, ChevronRight, Camera, Coffee, Users, Sun, ChevronDown, ChevronUp } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Uganda = () => {
  const [expandedPark, setExpandedPark] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const nationalParks = [
    {
      id: "bwindi",
      name: "Bwindi Impenetrable National Park",
      description: "Home to half of the world's mountain gorilla population and over 120 mammal species",
      image: "https://imgur.com/LMUcnkI.jpg",
      highlights: ["Gorilla Trekking", "Bird Watching", "Nature Walks", "Cultural Experiences"],
      bestTime: "June-August, December-February",
      extendedInfo: "A UNESCO World Heritage site covering 331 square kilometers of montane and lowland forest. The park is home to over 400 mountain gorillas, 350 bird species, and 200 butterfly species."
    },
    {
      id: "queen-elizabeth",
      name: "Queen Elizabeth National Park",
      description: "Famous for tree-climbing lions and diverse wildlife",
      image: "https://imgur.com/0MEx54w.jpg",
      highlights: ["Game Drives", "Boat Safaris", "Chimpanzee Tracking", "Bird Watching"],
      bestTime: "January-February, June-September",
      extendedInfo: "Uganda's most popular national park, covering 1,978 square kilometers. Features the Kazinga Channel, crater lakes, and over 95 mammal species."
    },
    {
      id: "murchison-falls",
      name: "Murchison Falls National Park",
      description: "Uganda's largest and oldest conservation area",
      image: "https://imgur.com/UTUoqF1.jpg",
      highlights: ["Waterfall Views", "Game Drives", "Boat Safaris", "Fishing"],
      bestTime: "December-February",
      extendedInfo: "Home to the world's most powerful waterfall and over 76 mammal species including elephants, giraffes, and lions."
    },
    {
      id: "kibale",
      name: "Kibale National Park",
      description: "Primate capital of the world with 13 primate species",
      image: "https://imgur.com/ePFOven.jpg",
      highlights: ["Chimpanzee Tracking", "Bird Watching", "Forest Walks", "Night Walks"],
      bestTime: "June-September, December-February",
      extendedInfo: "Home to over 1,500 chimpanzees and 375 bird species. The park covers 795 square kilometers of tropical rainforest."
    },
    {
      id: "kidepo-valley",
      name: "Kidepo Valley National Park",
      description: "Remote wilderness with unique wildlife",
      image: "https://imgur.com/nmErQ7p.jpg",
      highlights: ["Game Drives", "Cultural Visits", "Bird Watching", "Walking Safaris"],
      bestTime: "November-February",
      extendedInfo: "One of Africa's finest wildernesses, home to many species not found elsewhere in Uganda, including cheetahs and ostriches."
    },
    {
      id: "mgahinga",
      name: "Mgahinga Gorilla National Park",
      description: "Home to endangered mountain gorillas and golden monkeys",
      image: "https://imgur.com/kzmlWC2.jpg",
      highlights: ["Gorilla Tracking", "Golden Monkey Tracking", "Volcano Hiking", "Cultural Tours"],
      bestTime: "June-August, December-February",
      extendedInfo: "Uganda's smallest national park but part of the larger Virunga Conservation Area. Features three extinct volcanoes."
    },
    {
      id: "lake-mburo",
      name: "Lake Mburo National Park",
      description: "The 'Whispers of the Wild' with unique savanna wildlife",
      image: "https://imgur.com/TbfV0nT.jpg",
      highlights: ["Game Drives", "Boat Trips", "Cycling Safaris", "Walking Safaris"],
      bestTime: "June-August, December-February",
      extendedInfo: "The only park in Uganda with impalas and one of only two protecting zebras. Features five lakes and extensive acacia woodland."
    },
    {
      id: "mount-elgon",
      name: "Mount Elgon National Park",
      description: "An ancient extinct volcano with the largest volcanic base in the world",
      image: "https://imgur.com/w00VdsU.jpg",
      highlights: ["Mountain Climbing", "Cave Exploration", "Bird Watching", "Nature Walks"],
      bestTime: "June-August, December-March",
      extendedInfo: "Features the world's largest volcanic caldera and the second-highest mountain in Uganda. Rich in unique flora and fauna."
    },
    {
      id: "rwenzori",
      name: "Rwenzori Mountains National Park",
      description: "The legendary 'Mountains of the Moon' with Africa's third-highest peak",
      image: "https://imgur.com/gpipKD7.jpg",
      highlights: ["Mountain Climbing", "Hiking", "Bird Watching", "Cultural Visits"],
      bestTime: "December-February, June-August",
      extendedInfo: "A UNESCO World Heritage site featuring permanent glaciers, waterfalls, and unique alpine vegetation. Home to 70 mammal species."
    },
    {
      id: "semuliki",
      name: "Semuliki National Park",
      description: "A piece of Central African rainforest with hot springs",
      image: "https://imgur.com/CDOB8KF.jpg",
      highlights: ["Hot Springs", "Bird Watching", "Primate Viewing", "Nature Walks"],
      bestTime: "December-February, June-September",
      extendedInfo: "Features unique Central African wildlife and birds. Home to hot springs and the Batwa pygmy community."
    }
  ];

  const activities = [
    {
      name: "Gorilla Trekking",
      description: "Face-to-face encounters with mountain gorillas",
      image: "https://imgur.com/MyrxV4c.jpg",
      price: "From $700",
      duration: "1-4 hours",
      rating: 4.9
    },
    {
      name: "Wildlife Safari",
      description: "Explore diverse wildlife in stunning national parks",
      image: "https://imgur.com/bESqDRz.jpg",
      price: "From $200",
      duration: "1-5 days",
      rating: 4.8
    },
    {
      name: "Cultural Tours",
      description: "Immerse in local traditions and community life",
      image: "https://imgur.com/V4EbZrd.jpg",
      price: "From $50",
      duration: "Half day - Full day",
      rating: 4.7
    }
  ];

  const travelSeasons = [
    {
      name: "Dry Season (Dec-Feb)",
      description: "Perfect for wildlife viewing and gorilla trekking",
      activities: ["Safari", "Gorilla Trekking", "Hiking"],
      weather: "Warm and dry"
    },
    {
      name: "Long Rains (Mar-May)",
      description: "Lush landscapes and fewer tourists",
      activities: ["Bird Watching", "Photography", "Cultural Tours"],
      weather: "Frequent rainfall"
    },
    {
      name: "Dry Season (Jun-Aug)",
      description: "Peak season for wildlife and gorilla trekking",
      activities: ["Safari", "Gorilla Trekking", "Hiking"],
      weather: "Mild and dry"
    },
    {
      name: "Short Rains (Sep-Nov)",
      description: "Green season with occasional showers",
      activities: ["Bird Watching", "Photography", "Cultural Tours"],
      weather: "Intermittent rain"
    }
  ];

  return (
    <>
      <Navbar />
      
      {/* Hero Section - Mobile Optimized */}
      <section className="relative bg-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/TvdP6Mt.jpg"
            alt="Uganda landscape"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-8 sm:py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3">Welcome to Uganda</h1>
            <p className="text-base sm:text-xl text-white/90 mb-4">
              Discover the Pearl of Africa - a land of extraordinary wildlife, breathtaking landscapes, and warm, welcoming people.
            </p>
            
            <div className="grid grid-cols-2 gap-3 mt-6">
              <div className="text-center bg-black/20 backdrop-blur-sm rounded-lg p-2">
                <div className="text-xl sm:text-3xl font-bold">10</div>
                <div className="text-xs sm:text-sm text-white/80">National Parks</div>
              </div>
              <div className="text-center bg-black/20 backdrop-blur-sm rounded-lg p-2">
                <div className="text-xl sm:text-3xl font-bold">1,000+</div>
                <div className="text-xs sm:text-sm text-white/80">Bird Species</div>
              </div>
              <div className="text-center bg-black/20 backdrop-blur-sm rounded-lg p-2">
                <div className="text-xl sm:text-3xl font-bold">50%</div>
                <div className="text-xs sm:text-sm text-white/80">World's Gorillas</div>
              </div>
              <div className="text-center bg-black/20 backdrop-blur-sm rounded-lg p-2">
                <div className="text-xl sm:text-3xl font-bold">56</div>
                <div className="text-xs sm:text-sm text-white/80">Tribal Languages</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* National Parks Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">National Parks</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {nationalParks.map((park) => (
              <div key={park.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300">
                <div className="relative h-32">
                  <img 
                    src={park.image} 
                    alt={park.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-2 left-2 text-white">
                    <h3 className="text-sm font-medium line-clamp-1">{park.name}</h3>
                  </div>
                </div>
                
                <div className="p-3">
                  <p className="text-xs text-gray-600 mb-2 line-clamp-2">{park.description}</p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center text-xs text-gray-700">
                      <Calendar className="h-3 w-3 text-ta-green mr-1" />
                      Best: {park.bestTime}
                    </div>
                    
                    <div className="flex flex-wrap gap-1">
                      {park.highlights.slice(0, 2).map((highlight, i) => (
                        <span 
                          key={i}
                          className="px-1.5 py-0.5 bg-ta-green/10 text-ta-green rounded-full text-[10px]"
                        >
                          {highlight}
                        </span>
                      ))}
                      {park.highlights.length > 2 && (
                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[10px]">
                          +{park.highlights.length - 2} more
                        </span>
                      )}
                    </div>
                    
                    <button
                      onClick={() => setExpandedPark(expandedPark === park.id ? null : park.id)}
                      className="w-full mt-2 flex items-center justify-center text-xs text-ta-green hover:text-ta-green-dark transition-colors"
                    >
                      {expandedPark === park.id ? (
                        <>
                          Show Less
                          <ChevronUp className="h-3 w-3 ml-1" />
                        </>
                      ) : (
                        <>
                          See More
                          <ChevronDown className="h-3 w-3 ml-1" />
                        </>
                      )}
                    </button>
                  </div>
                  
                  {expandedPark === park.id && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-600">{park.extendedInfo}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {park.highlights.slice(2).map((highlight, i) => (
                          <span 
                            key={i}
                            className="px-1.5 py-0.5 bg-ta-green/10 text-ta-green rounded-full text-[10px]"
                          >
                            {highlight}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Activities Section - Mobile Optimized */}
      <section className="py-8 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Popular Activities</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-8">
            {activities.map((activity, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group">
                <div className="relative h-40 sm:h-48">
                  <img 
                    src={activity.image} 
                    alt={activity.name}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center">
                    <Star className="h-3 w-3 text-yellow-400 fill-current" />
                    <span className="ml-1 text-xs font-medium">{activity.rating}</span>
                  </div>
                </div>
                
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{activity.name}</h3>
                  <p className="text-sm text-gray-600 mb-2">{activity.description}</p>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="text-ta-green font-medium">{activity.price}</div>
                    <div className="text-gray-500">{activity.duration}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Seasons - Mobile Optimized */}
      <section className="py-8 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Best Time to Visit</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {travelSeasons.map((season, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center mb-2">
                  <Sun className="h-5 w-5 text-ta-green" />
                  <h3 className="text-base font-semibold text-gray-900 ml-2">{season.name}</h3>
                </div>
                
                <p className="text-sm text-gray-600 mb-3">{season.description}</p>
                
                <div className="space-y-2">
                  <div className="flex items-center text-xs text-gray-700">
                    <Calendar className="h-3 w-3 text-ta-green mr-1" />
                    {season.weather}
                  </div>
                  
                  <div className="flex flex-wrap gap-1">
                    {season.activities.map((activity, i) => (
                      <span 
                        key={i}
                        className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded-full text-[10px]"
                      >
                        {activity}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Tips - Mobile Optimized */}
      <section className="py-8 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Travel Tips</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center mb-3">
                <Shield className="h-5 w-5 text-ta-green" />
                <h3 className="text-base font-semibold text-gray-900 ml-2">Health & Safety</h3>
              </div>
              <ul className="space-y-1.5 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Get necessary vaccinations
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Carry malaria prevention medication
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Drink bottled or filtered water
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Travel insurance is recommended
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center mb-3">
                <Camera className="h-5 w-5 text-ta-green" />
                <h3 className="text-base font-semibold text-gray-900 ml-2">What to Pack</h3>
              </div>
              <ul className="space-y-1.5 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Light, breathable clothing
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Sturdy hiking boots
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Insect repellent
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Camera and binoculars
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center mb-3">
                <Coffee className="h-5 w-5 text-ta-green" />
                <h3 className="text-base font-semibold text-gray-900 ml-2">Local Customs</h3>
              </div>
              <ul className="space-y-1.5 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Dress modestly
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Ask before taking photos
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Greet people with respect
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Learn basic local phrases
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Uganda;